import {
  Dispatch,
  ReactElement,
  SetStateAction,
  Suspense,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cc from 'classcat';
import { SCORE_MULTIPLIER } from 'utils/constants/app/magic-numbers-app';
import { ColSizes, EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy as lazyImport } from 'utils/generic/lazy';
import {
  ESPORTS_ADDITIONAL_DATA,
  isESportMOBAGames
} from 'utils/helpers/moba-games-statistics-options';
import { isMobile } from 'utils/is-mobile';
import { checkIsNotESportType } from 'utils/sportsbook/check-type-is-not-sportbook';
import { ETeamNumber } from 'interfaces/sportsbook';
import {
  Game,
  GameInfoAdditionalDataMap
} from 'interfaces/sportsbook-data-levels';
import { AdditionalStatisticsFilterSkeleton } from './AdditionalStatisticsFilter/AdditionalStatisticsFilterSkeleton';
import { MOBAGamesStatisticsScoresSkeleton } from './MOBAGames/Skeleton/MOBAGamesStatisticsScoresSkeleton';
import { CounterStrikeStatisticsSkeleton } from 'components/GameDetails/AdditionalStatistics/CounterStrikeStatistics/Skeleton';
import { GlobalIcon } from 'components/GlobalIcon';
import { TeamCaption } from 'components/TeamCaption';
import { Col } from 'newcomponents/UI/Col';
import { Row } from 'newcomponents/UI/Row';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useSearchParams } from 'hooks/useSearchParams';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setAdditionalStatisticsData } from 'store/actions';
import { getAdditionalStatisticsData } from 'store/selectors/e-sport-data';
import '../Simple/index.less';
import { Logo, Score, TeamInfo } from '../Simple/style';
import './index.less';

const { CounterStrikeStatistics } = lazyImport(
  () =>
    import(
      /* webpackChunkName: "counter-strike-statistics" */ 'components/GameDetails/AdditionalStatistics/CounterStrikeStatistics'
    )
);

const { MOBAGamesStatistics } = lazyImport(
  () =>
    import(
      /* webpackChunkName: "moba-games-statistics" */ 'components/GameDetails/AdditionalStatistics/MOBAGames'
    )
);

const { AdditionalStatisticsFilter } = lazyImport(
  () =>
    import(
      /* webpackChunkName: "additional-statistics-filter" */ 'components/GameDetails/AdditionalStatistics/AdditionalStatisticsFilter'
    )
);

export const ADDITIONAL_STATISTICS_LAYOUTS = new Proxy(
  {
    [ESPORTS_ADDITIONAL_DATA.CounterStrike.alias]: {
      view: CounterStrikeStatistics,
      skeleton: CounterStrikeStatisticsSkeleton
    },
    MOBAGames: {
      view: MOBAGamesStatistics,
      skeleton: MOBAGamesStatisticsScoresSkeleton
    }
  },
  {
    get: function (target, prop) {
      if (isESportMOBAGames(prop as string)) {
        return target.MOBAGames;
      }

      return target[prop as string];
    }
  }
);

type TAdditionalStatisticsProps = {
  game: Game;
  isLive: boolean;
};

export const AdditionalStatistics = ({
  game,
  isLive
}: TAdditionalStatisticsProps): ReactElement => {
  const dispatch = useDispatch();
  const { type } = useSearchParams();
  const {
    params: { sport }
  } = useSbRouteMatch();

  const additionalStatisticsData = useSelector(getAdditionalStatisticsData);

  const isLivePage = type === 'live' || isLive;
  const isOuthright = game.show_type === 'OUTRIGHT';
  const teamCaptionSize = isMobile() ? 'sm' : 'lg';
  const currentMapData = ESPORTS_ADDITIONAL_DATA[game.sport_alias || sport];

  const isMOBAGames = isESportMOBAGames(game.sport_alias || sport);

  const [isOpenStatistics, setIsOpenStatistics] = useState(
    isLivePage && Object.keys(ESPORTS_ADDITIONAL_DATA).includes(sport)
  );

  useEffect(() => {
    const maps = game.info?.additional_data?.[currentMapData.maps];
    const selected =
      additionalStatisticsData.selected === -1
        ? game.info?.additional_data?.[currentMapData.current] || -1
        : additionalStatisticsData.selected;

    const map: GameInfoAdditionalDataMap | undefined = (maps || []).find(
      map => Number(map[currentMapData.number]) === Number(selected)
    );

    if (maps?.length) {
      maps[maps.length - 1].timers = game.info?.additional_data?.timers || [];
    }

    dispatch(setAdditionalStatisticsData({ maps, selected, map }));
  }, [game.info?.additional_data, currentMapData]);

  useEffect(() => {
    return () => {
      dispatch(
        setAdditionalStatisticsData({
          selected: -1,
          map: undefined,
          maps: undefined
        })
      );
    };
  }, []);

  const Layout = useMemo(() => {
    return ADDITIONAL_STATISTICS_LAYOUTS[game.sport_alias || sport];
  }, [game.sport_alias, sport]);

  const [team1Color, team2Color] = useMemo(() => {
    const teamColors = additionalStatisticsData.map?.isHomeBlue
      ? ['blue', 'red']
      : ['red', 'blue'];

    if (SpringConfigs.IS_RTL) {
      teamColors.reverse();
    }

    return teamColors;
  }, [additionalStatisticsData.map?.isHomeBlue]);

  const gameTextInfo = () => {
    const mapsData = additionalStatisticsData.maps || [];
    const team1ClassColor = `eSportGame__team1__mobaGames--${team1Color}`;
    const team2ClassColor = `eSportGame__team1__mobaGames--${team2Color}`;

    let text = `<span class="eSportGame__team1 ${
      isMOBAGames ? team1ClassColor : ''
    }">${game.info?.score1 || 0}</span>:<span class="eSportGame__team2 ${
      isMOBAGames ? team2ClassColor : ''
    }">${game.info?.score2 || 0}</span>`;

    let teams: {
      [key: number]: number;
    } = {};

    mapsData.forEach(map => {
      teams = { 1: 0, 2: 0 };

      Object.values(map[currentMapData.outcome] || []).forEach(
        (round, index) => {
          if (typeof round === 'number') {
            teams[index + 1] = round;
          } else {
            teams[round.wonSide] += 1;
          }
        }
      );

      text += `, ( <span class="eSportGame__team1 ${
        isMOBAGames ? team1ClassColor : ''
      }">${teams[1]}</span>:<span class="eSportGame__team2 ${
        isMOBAGames ? team2ClassColor : ''
      }">${teams[2]}</span> )`;
    });

    return text;
  };

  return (
    <>
      <Row align="center" className={isMobile() ? 'simpleRowMob' : 'simpleRow'}>
        <Col
          xs={
            isOuthright
              ? isMobile()
                ? ColSizes.SIZE_24
                : ColSizes.SIZE_14
              : ColSizes.SIZE_12
          }
        >
          <TeamInfo
            className={cc([
              { 'team__info-pad-right': game.info?.score1?.length }
            ])}
            paddingRight={game.info?.score1?.length * SCORE_MULTIPLIER}
          >
            {SpringConfigs.TEAM_LOGOS && (
              <Logo isFirstLogo>
                <TeamCaption
                  reverse={!isMobile()}
                  logo
                  teamId={game.team1_id}
                  name={game.team1_name}
                  teamNumber={ETeamNumber.HOME}
                  isNotESport={checkIsNotESportType(game)}
                  dontShowTitle={true}
                  size={isMobile() ? 'lg' : teamCaptionSize}
                />
              </Logo>
            )}
            <TeamCaption
              name={game.team1_name}
              teamNumber={ETeamNumber.HOME}
              isNotESport={checkIsNotESportType(game)}
              size={teamCaptionSize}
              reverse={!isMobile()}
            />
          </TeamInfo>
        </Col>
        {!isOuthright && (
          <Col xs={ColSizes.SIZE_12}>
            <Score
              className={cc([
                'esport__score',
                {
                  'esport__score--withoutTeamLogo': !SpringConfigs.TEAM_LOGOS
                }
              ])}
            >
              {game.info?.score1} : {game.info?.score2}
            </Score>
            <TeamInfo
              className={cc([
                {
                  'team__info-pad-left':
                    game.info?.score2?.length * SCORE_MULTIPLIER
                }
              ])}
              paddingLeft={game.info?.score2?.length * SCORE_MULTIPLIER}
            >
              {SpringConfigs.TEAM_LOGOS && (
                <Logo>
                  <TeamCaption
                    size={isMobile() ? 'lg' : teamCaptionSize}
                    logo
                    teamId={game.team2_id}
                    name={game.team2_name}
                    teamNumber={ETeamNumber.AWAY}
                    isNotESport={checkIsNotESportType(game)}
                    dontShowTitle={true}
                  />
                </Logo>
              )}
              <TeamCaption
                name={game.team2_name}
                teamNumber={ETeamNumber.AWAY}
                isNotESport={checkIsNotESportType(game)}
                size={teamCaptionSize}
              />
            </TeamInfo>
          </Col>
        )}
        {isLivePage ? (
          <Col
            className={isMobile() ? `ESportText-infoMob` : 'ESportText-info'}
          >
            {game.info ? (
              <div dangerouslySetInnerHTML={{ __html: gameTextInfo() }} />
            ) : (
              <Skeleton.Input
                style={{
                  borderRadius: 4,
                  height: 20,
                  margin: '4px 0 0 0',
                  justifyContent: 'center'
                }}
              />
            )}

            <AdditionalStatisticsArrowIcon
              isOpenStatistics={isOpenStatistics}
              setIsOpenStatistics={setIsOpenStatistics}
            />
          </Col>
        ) : null}

        {isMobile() && isOpenStatistics ? (
          <Suspense
            fallback={
              <div style={{ width: '100%' }}>
                {isLivePage && !game.text_info?.length && (
                  <div className="gameDetailsSkeleton__options gameDetailsSkeleton__options--scores">
                    <Skeleton.Button
                      active
                      style={{ width: '70%', height: 22 }}
                    />
                  </div>
                )}

                <div className="gameDetailsSkeleton__options">
                  <Skeleton.Button
                    active
                    style={{ width: '100%', height: 40 }}
                  />
                </div>
              </div>
            }
          >
            <Suspense
              fallback={
                isLivePage && <AdditionalStatisticsFilterSkeleton block />
              }
            >
              <AdditionalStatisticsFilter sportAlias={game.sport_alias || ''} />
            </Suspense>
          </Suspense>
        ) : null}
      </Row>
      <Suspense fallback={isOpenStatistics && <Layout.skeleton />}>
        <Layout.view
          expanded={isOpenStatistics}
          sportAlias={game.sport_alias || sport}
          teams={{
            1: {
              id: game.team1_id,
              name: game.team1_name
            },
            2: {
              id: game.team2_id,
              name: game.team2_name
            }
          }}
        />
      </Suspense>
    </>
  );
};

type TAdditionalStatisticsArrowIcon = {
  isOpenStatistics: boolean;
  setIsOpenStatistics: Dispatch<SetStateAction<boolean>>;
};

const AdditionalStatisticsArrowIcon = ({
  isOpenStatistics,
  setIsOpenStatistics
}: TAdditionalStatisticsArrowIcon): ReactElement => {
  return (
    <div className="eSportGame__filter-caret-container">
      <GlobalIcon
        lib="generic"
        name="caretDown"
        theme="default"
        size={EIconSize._16}
        onClick={() => setIsOpenStatistics(prev => !prev)}
        className={cc([
          `eSportGame__filter-caret ${
            SpringConfigs.IS_RTL ? 'expanded-flip' : ''
          }`,
          {
            'eSportGame__filter-caret--mobile': !isMobile(),
            'eSportGame__filter-caret-expanded': isOpenStatistics
          }
        ])}
      />
    </div>
  );
};
